import {
  Container,
  Button,
  Card,
  TableContainer,
  Table,
  TableBody,
  Tooltip,
  Box,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';

import { Link as RouterLink } from 'react-router-dom';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { useSettingsContext } from '../../../components/settings';
import TillTableToolbar from './TillTableToolbar';
import Scrollbar from '../../../components/scrollbar/Scrollbar';
import {
  TableHeadCustom,
  TableEmptyRows,
  TableNoData,
  TablePaginationCustom,
  emptyRows,
  useTable,
} from '../../../components/table';
import TillTableRow from './TillTableRow';
import { statusOptionsProps } from '../../../@types/till';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useTillStore from '../../../providers/useTillStore';
import { TillService } from '../../../api/till.service';
import useBarcodeScanner from '../../../hooks/useBarcodeScanner';
import { ProductService } from '../../../api/product.service';
import { enqueueSnackbar } from '../../../components/snackbar';
import {
  TILL_BARCODE_NAV_STATE_KEY,
  activeProductToDispenseLine,
  findProductByBarcode,
} from './tillBarcodeShared';
import { getLatestTillLineStatus } from './tillListShared';
import { RoleBasedSection } from '../../../components/rbac';
import { RoleGroup } from '../../../auth/types/roles';

const TILL_ACTION_ROLES = [
  ...RoleGroup.BOOKING_STAFF,
  ...RoleGroup.MANAGEMENT_STAFF,
];

export default function TillListPage() {
  const { removeFromTill } = useTillStore();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [statusValues, setStatusValues] = useState<statusOptionsProps[]>([]);
  const [tills, setTills] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [scanProducts, setScanProducts] = useState<any[]>([]);

  const { themeStretch } = useSettingsContext();

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    setSelected,
    onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrderBy: 'startTime' });

  const fetchTills = async () => {
    try {
      const tillService = new TillService();
      const res = await tillService.getAllTills({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (res.data?.data) {
        setTills(res.data.data);
        setTotalRecords(res.data.totalRecords ?? res.data.data.length);
      } else {
        setTills(res.data || []);
        setTotalRecords((res.data || []).length || 0);
      }
    } catch (error) {
      console.error('Failed to fetch tills', error);
      setTills([]);
      setTotalRecords(0);
    }
  };

  useEffect(() => {
    fetchTills();
  }, [page, rowsPerPage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await new ProductService().getActiveProducts();
        if (!cancelled) {
          setScanProducts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(
          'Failed to fetch products for till barcode scanner',
          error,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTillBarcodeScan = useCallback(
    (barcode: string) => {
      const product = findProductByBarcode(scanProducts, barcode);
      if (product) {
        navigate(PATH_DASHBOARD.till.new, {
          state: {
            [TILL_BARCODE_NAV_STATE_KEY]: activeProductToDispenseLine(product),
          },
        });
        enqueueSnackbar(`Opening Create New Sale with ${product.name}`, {
          variant: 'success',
        });
      } else {
        setSearchValue(barcode);
        setPage(0);
        enqueueSnackbar(`No product found for code: ${barcode}`, {
          variant: 'warning',
        });
      }
    },
    [scanProducts, navigate, setPage],
  );

  useBarcodeScanner({ onScan: handleTillBarcodeScan });

  // Filtering
  const dataFiltered = useMemo(() => {
    let list = tills;

    if (searchValue) {
      const search = searchValue.toLowerCase();
      list = list.filter((item) => {
        const fullName = [item.patient?.firstName, item.patient?.surName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const phone = item.patient?.phone?.toLowerCase?.() || '';
        const email = item.patient?.email?.toLowerCase?.() || '';

        return (
          fullName.includes(search) ||
          phone.includes(search) ||
          email.includes(search)
        );
      });
    }

    if (statusValues.length) {
      const statuses = statusValues.map((s) => s.value);
      list = list.filter((item) => {
        const lastStatus = getLatestTillLineStatus(item);
        return lastStatus != null && statuses.includes(lastStatus);
      });
    }

    return list;
  }, [tills, searchValue, statusValues]);

  const TABLE_HEAD = [
    { id: 'patientName', label: 'Patient Name', align: 'center' },
    { id: 'patientId', label: 'Patient ID', align: 'center' },
    { id: 'totalTills', label: 'Total Tills', align: 'center' },
    { id: 'lastTillDate', label: 'Last Till Date', align: 'center' },
    { id: 'lastTillStatus', label: 'Status', align: 'center' },
    { id: '', label: 'Actions', align: 'center' },
  ];

  return (
    <>
      <Helmet>
        <title>Till</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : 'xl'}>
        <CustomBreadcrumbs
          heading="Till"
          links={[
            {
              name: 'Dashboard',
              href: PATH_DASHBOARD.root,
            },
            {
              name: 'POS',
              href: PATH_DASHBOARD.till.list,
            },
          ]}
          action={
            <RoleBasedSection allowedRoles={TILL_ACTION_ROLES}>
              <Box>
                <Tooltip title={''}>
                  <span>
                    <Button
                      component={RouterLink}
                      to={PATH_DASHBOARD.till.external}
                      sx={{ marginRight: '15px' }}
                      variant="contained"
                    >
                      <span>External Sale</span>
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={''}>
                  <span>
                    <Button
                      component={RouterLink}
                      to={PATH_DASHBOARD.till.new}
                      sx={{ marginRight: '15px' }}
                      variant="contained"
                    >
                      <span>Create New Sale</span>
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </RoleBasedSection>
          }
        />

        <Card>
          <TillTableToolbar
            onSearchFilter={(value: string) => {
              setSearchValue(value);
              setPage(0);
            }}
            searchValue={searchValue}
            statusValues={statusValues}
            onResetFilter={() => {
              setSearchValue('');
              setStatusValues([]);
              setPage(0);
            }}
            onStatusFilter={(options: statusOptionsProps[]) => {
              setStatusValues(options);
              setPage(0);
            }}
          />
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={dataFiltered.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      dataFiltered.map((row: any) => row.patient?.id),
                    )
                  }
                />

                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((till: any) => (
                      <TillTableRow key={till.id} till={till} />
                    ))}
                  <TableEmptyRows
                    height={63}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      dataFiltered.length,
                    )}
                  />
                  {!dataFiltered.length && (
                    <TableNoData isNotFound={dataFiltered.length <= 0} />
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={totalRecords}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            dense={dense}
            onChangeDense={onChangeDense}
          />
        </Card>
      </Container>
    </>
  );
}
